import { GetQSetByIdDocument, useChangeItemSetQuestionMutation } from '@exam-management/graphql';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver, type PropsWithChildren } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import {
  type ItemSetQuestionChangeFormInput,
  ItemSetQuestionChangeSchema,
} from './item-set-question-change-schema';

interface ItemSetQuestionChangeFormProviderProps extends PropsWithChildren {
  qSetQuestionId: string;
  onComplete?: () => void;
}

export function ItemSetQuestionChangeFormProvider(
  props: Readonly<ItemSetQuestionChangeFormProviderProps>,
) {
  const { children, qSetQuestionId, onComplete } = props;

  const { showSnackbar } = useSnackbar();

  const formContext = useForm({
    defaultValues: {
      questionGroupId: '',
    },
    resolver: zodResolver(ItemSetQuestionChangeSchema),
  });

  const [changeItemSetQuestion] = useChangeItemSetQuestionMutation({
    onCompleted: (data) => {
      const order = data.excChangeItemSetQuestion.qSetQuestions
        .map((qSetQuestion) => qSetQuestion.order)
        .sort((a, b) => a - b)
        .join(', ');

      showSnackbar({
        message: `Question No. ${order} has been replaced successfully`,
        variant: 'success',
      });
      onComplete?.();
    },
    onError: () => {
      showSnackbar({ message: 'Change question failed, please try again later', variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [GetQSetByIdDocument],
  });

  const handleItemSetQuestionChange = async (data: ItemSetQuestionChangeFormInput) => {
    await changeItemSetQuestion({
      variables: {
        changeItemSetQuestionInput: {
          qSetQuestionId: qSetQuestionId,
          questionGroupId: data.questionGroupId,
        },
      },
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleItemSetQuestionChange)}>{children}</Form>
    </FormProvider>
  );
}
